/**
 * COMPTABILITÉ — DÉPLACER UN RAPPORT ZAP ENTIER VERS UNE AUTRE NUIT.
 *
 * Le 09/10/2026, une caisse non clôturée le mercredi a produit une synthèse
 * du mer. 07/10 15:35 au ven. 09/10 10:01 : rangée au mercredi, alors que les
 * ventes étaient celles du jeudi. Ce geste déplace le RAPPORT : la synthèse,
 * ses pièces (même préfixe, même courriel), leurs tickets et leurs produits,
 * en une fois, avec un motif (`POST /api/v1/finance/sources/:id/move`,
 * `finance.source.manage`). Il est offert sur la fiche de la nuit ET sur la
 * fiche de la synthèse, où il remplace « Rattacher à cette nuit » : ce
 * dernier ne déplaçait que le fichier, et le serveur le refuse désormais sur
 * un rapport ZAP (revue du 09/10/2026).
 *
 * Le serveur décide de tout : les nuits que la période couvre
 * (`nights_covered`, proposées en premier), et les refus — nuit clôturée,
 * nuit validée, montants déjà posés dans une clôture — dont la phrase dit
 * quoi faire. L'écran ne recalcule rien.
 */

import { useState } from 'react'
import { CalendarClock, X } from 'lucide-react'
import { DatePicker } from '../admin/pickers.js'
import { Modal, adminMutate, useToast } from '../admin/primitives.js'
import { navigate } from '../router.js'
import { MotifField, ajouterJours, aujourdhuiParis, motifSuffisant, nuitCourte, nuitLongue } from './ClosingCommon.js'
import './finance-nights.css'

/** « mer. 07/10 15:35 », à l'heure de Paris : la période d'un rapport qui déborde sur plusieurs jours. */
const FMT_INSTANT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
})
export function instantParis(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : FMT_INSTANT.format(d)
}

interface ReponseDeplacement {
  error?: string
  message?: string
  to_business_date?: string
  pieces?: number
  unchanged?: boolean
  moved?: { documents?: number; tickets?: number; products?: number; offers?: number }
  withdrawn_sources?: Array<{ business_date?: string; lines?: number; back_to_draft?: boolean }>
  reread?: { status?: string } | null
}

/** Le bilan, en une phrase : ce qui a bougé, et le signalement levé ou non. */
function bilan(r: ReponseDeplacement, vers: string): string {
  if (r.unchanged) return `Le rapport était déjà sur la nuit du ${nuitCourte(vers)} : rien n’a changé.`
  const m = r.moved ?? {}
  const relu = r.reread?.status === 'PARSED' ? ' La synthèse a été relue : plus rien à trancher.' : ''
  // Une clôture en cours qui avait reçu ces montants les a perdus : le dire,
  // et dire comment les reposer.
  const retraits = (r.withdrawn_sources ?? []).filter((x) => typeof x.business_date === 'string')
  const retire = retraits.length === 0 ? ''
    : ` Les montants ZAP de la clôture ${retraits.length > 1 ? 'des nuits' : 'de la nuit'} du ${retraits.map((x) => nuitCourte(x.business_date!)).join(', ')} ont été retirés${retraits.some((x) => x.back_to_draft) ? ' (elle repasse en brouillon)' : ''} : « Reprendre » sur ${retraits.length > 1 ? 'ces clôtures' : 'cette clôture'} repose ceux des rapports de la nuit.`
  if ((m.documents ?? 0) + (m.tickets ?? 0) + (m.products ?? 0) + (m.offers ?? 0) === 0) {
    return `Nuit du ${nuitCourte(vers)} confirmée pour ce rapport.${relu}${retire}`
  }
  const pieces = (r.pieces ?? 1) - 1
  const parts = [
    pieces > 0 ? `${pieces} pièce${pieces > 1 ? 's' : ''} du même rapport` : null,
    (m.tickets ?? 0) > 0 ? `${m.tickets} ticket${(m.tickets ?? 0) > 1 ? 's' : ''}` : null,
    (m.products ?? 0) > 0 ? `${m.products} ligne${(m.products ?? 0) > 1 ? 's' : ''} de produits` : null,
  ].filter((x): x is string => x !== null)
  return `Rapport déplacé vers la nuit du ${nuitCourte(vers)}${parts.length > 0 ? `, avec ${parts.join(', ')}` : ''}.${relu}${retire}`
}

export function DeplacerRapport(props: {
  documentId: string
  /** La nuit où le rapport est rangé aujourd'hui ; `null` : aucune encore. */
  nuit: string | null
  /** Les nuits que sa période couvre, d'après le serveur. */
  couvertes: string[]
  periode: { debut: string; fin: string } | null
  /**
   * Après le geste, ouvrir la fiche de la nuit d'arrivée (la fiche d'une nuit,
   * que le rapport quitte) ; `false` : rester et relire (la fiche du document).
   */
  suivre?: boolean
  /** Relire l'écran quand on y reste. */
  onChange: () => void
}) {
  const toast = useToast()
  const plusieurs = props.couvertes.length > 1
  const autres = props.couvertes.filter((n) => n !== props.nuit)
  const proposee = autres[0] ?? (props.nuit === null ? aujourdhuiParis() : ajouterJours(props.nuit, 1))
  const [ouvert, setOuvert] = useState(false)
  const [vers, setVers] = useState(proposee)
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const fermer = () => { setOuvert(false); setMotif(''); setVers(proposee) }
  const valider = async () => {
    setEnvoi(true)
    const r = await adminMutate<ReponseDeplacement>(
      'POST', `/api/v1/finance/sources/${props.documentId}/move`, { business_date: vers, reason: motif.trim() })
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', bilan(r.data, vers))
      fermer()
      // La fiche de nuit suit le rapport : c'est sur l'autre nuit qu'il se lit
      // désormais. La fiche du document, elle, reste et se relit.
      const arrivee = r.data.to_business_date ?? vers
      if (arrivee === props.nuit || props.suivre === false) props.onChange()
      else navigate(`/comptabilite/nuits/${arrivee}`)
      return
    }
    // Un refus du serveur (4xx) dit ce qui n'a pas été fait ; une panne (5xx,
    // réseau) ne dit RIEN de ce qui a été écrit : ne jamais affirmer « rien
    // n'a été modifié » sans le savoir (revue du 09/10/2026).
    if (r.status >= 400 && r.status < 500) {
      toast('error', r.data.message ?? 'Le déplacement a été refusé : rien n’a été modifié.')
      return
    }
    toast('error', 'Le serveur n’a pas confirmé le déplacement : rechargez la fiche pour voir sur quelle nuit est le rapport avant de recommencer.')
    props.onChange()
  }

  return (
    <>
      <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setOuvert(true)}>
        <CalendarClock size={14} aria-hidden="true" /> Déplacer
      </button>

      <Modal open={ouvert} onClose={fermer} owning width={560} ariaLabel="Déplacer le rapport vers une autre nuit">
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true"><CalendarClock size={22} /></span>
          <div>
            <div className="lc-modal__title">Déplacer le rapport vers une autre nuit</div>
          </div>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={fermer} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <div className="lc-modal__body fiw-modal">
          <p className="lc-hint">
            {props.nuit === null ? <>Rangé aujourd’hui sur aucune nuit</> : <>Rangé aujourd’hui sur la nuit du {nuitLongue(props.nuit)}</>}
            {props.periode ? <>, période du {instantParis(props.periode.debut)} au {instantParis(props.periode.fin)}</> : null}.
            {props.couvertes.length > 1 ? <> Cette période couvre {props.couvertes.length} nuits.</> : null}
          </p>

          {/* Les nuits de la période d'abord — la nuit actuelle comprise : la
              garder est aussi une décision, qui lève le signalement. */}
          {plusieurs ? (
            <div className="fn-deplacer__nuits">
              <span className="fn-deplacer__label" id={`dep-nuits-${props.documentId}`}>Nuits couvertes par la période</span>
              <div className="lc-seg" role="radiogroup" aria-labelledby={`dep-nuits-${props.documentId}`}>
                {props.couvertes.map((n) => (
                  <button key={n} type="button" role="radio" aria-checked={vers === n}
                    className={`lc-seg__btn ${vers === n ? 'lc-seg__btn--on' : ''}`} onClick={() => setVers(n)}>
                    {nuitCourte(n)}{n === props.nuit ? ' (actuelle)' : ''}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="fn-deplacer__nuits">
            <span className="fn-deplacer__label">{plusieurs ? 'Ou une autre nuit' : 'Vers la nuit du'}</span>
            <DatePicker value={vers} onPick={setVers} label={nuitLongue(vers)} ariaLabel="Choisir la nuit d’arrivée" />
          </div>

          <p className="lc-hint">
            {vers === props.nuit
              ? <>Le rapport reste sur la nuit du {nuitLongue(vers)}, et ce choix est enregistré : la relecture ne le contestera plus.</>
              : <>La synthèse, les pièces du même rapport (détails de caisse, doseurs, TVA…), leurs tickets et leurs produits
                passent ensemble sur la nuit du {nuitLongue(vers)}. Refusé si l’une des deux nuits a une clôture finalisée ou une
                validation en vigueur. Une clôture en cours qui avait reçu ces montants les perd : « Reprendre » y repose ceux
                des rapports de sa nuit.</>}
          </p>

          <MotifField
            id={`motif-deplacer-${props.documentId}`}
            valeur={motif}
            onChange={setMotif}
            label="Pourquoi ce rapport change-t-il de nuit ? (par exemple « caisse non clôturée le mercredi, ventes du jeudi »)"
            aide="Ce motif reste attaché au rapport, dans l’historique."
          />
          <div className="fiw-modal__actions">
            <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer}>Annuler</button>
            <button type="button" className="lc-btn lc-btn--primary"
              disabled={!motifSuffisant(motif) || envoi}
              onClick={() => void valider()}>
              {envoi ? 'Enregistrement en cours' : vers === props.nuit ? `Garder la nuit du ${nuitCourte(vers)}` : `Déplacer vers le ${nuitCourte(vers)}`}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
